import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, DollarSign, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REFERRAL_SOURCES = [
  { id: 'referral-friend', value: 'Referral from friend/colleague', label: 'Referral from friend/colleague' },
  { id: 'referral-client', value: 'Referral from current client', label: 'Referral from current client' },
  { id: 'google-search', value: 'Google search', label: 'Google search' },
  { id: 'linkedin', value: 'LinkedIn', label: 'LinkedIn' },
  { id: 'facebook', value: 'Facebook', label: 'Facebook' },
  { id: 'local-event', value: 'Local event/networking', label: 'Local event/networking' },
  { id: 'online-directory', value: 'Online directory', label: 'Online directory' },
  { id: 'other', value: 'Other', label: 'Other' }
];

const BookConsultModal = ({ isOpen, onClose, isReferral = false }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    users: '',
    message: '',
    referralSource: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        referral_source: formData.referralSource,
        is_referral: isReferral
      };
      delete payload.referralSource;

      await axios.post(`${API_URL}/api/contact`, payload);
      
      // Store name for thank you page and close modal
      const submittedName = formData.name;
      setFormData({ name: '', email: '', phone: '', company: '', users: '', message: '', referralSource: '' });
      onClose();
      
      // Navigate to thank you page with state
      navigate('/thank-you', { 
        state: { 
          isReferral, 
          name: submittedName 
        } 
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      referralSource: value
    });
  };

  const title = isReferral ? "Refer a Business" : "Book a Free Consultation";
  const description = isReferral 
    ? "Tell us about the business you're referring and earn $300 when they become a client."
    : "Tell us about your IT needs and we'll schedule a call to discuss how we can help.";
  const Icon = isReferral ? DollarSign : Calendar;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center">
            <Icon className="w-6 h-6 text-blue-600 mr-2" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{isReferral ? "Your Name *" : "Full Name *"}</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Doe"
                data-testid="contact-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{isReferral ? "Your Email *" : "Email *"}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@company.com"
                data-testid="contact-email-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">{isReferral ? "Your Phone *" : "Phone Number *"}</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="(774) 644-7101"
                data-testid="contact-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">{isReferral ? "Referred Business Name" : "Company Name"}</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder={isReferral ? "Business you're referring" : "Your Company"}
                data-testid="contact-company-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="users">{isReferral ? "Estimated Number of Users" : "Number of Users"}</Label>
            <Input
              id="users"
              name="users"
              type="number"
              value={formData.users}
              onChange={handleChange}
              placeholder="e.g., 15"
              data-testid="contact-users-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{isReferral ? "Tell us about this referral *" : "What can we help you with? *"}</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={4}
              placeholder={isReferral 
                ? "Tell us about the business and their IT needs..." 
                : "Tell us about your IT needs, challenges, or questions..."}
              data-testid="contact-message-input"
            />
          </div>

          {!isReferral && (
            <div className="space-y-2">
              <Label htmlFor="referralSource">How did you hear about us?</Label>
              <Select value={formData.referralSource} onValueChange={handleSelectChange}>
                <SelectTrigger data-testid="contact-referral-source-select">
                  <SelectValue placeholder="Select an option..." />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_SOURCES.map((source) => (
                    <SelectItem key={source.id} value={source.value}>
                      {source.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>What happens next?</strong><br />
              {isReferral 
                ? "We'll reach out to the business you referred. Once they become a client and complete their first month of service, you'll receive $300!"
                : "We'll review your request and contact you within 1 business day (often faster) to schedule your free consultation."}
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={isSubmitting}
              data-testid="contact-submit-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookConsultModal;
